import { SentryService } from '@/plugins/sentry'
import axios, { AxiosError, AxiosRequestConfig, AxiosRequestHeaders } from 'axios'
import { getAuthPort, tryGetAuthPort } from '@/shared/api/auth-port'
import { getApiBaseUrl } from '@/shared/api/base-url'
import { isAuthEndpoint } from '@/shared/config/featureFlags'
import { friendlyHttpMessage } from '@/shared/api/errors'

const UPLOAD_TIMEOUT_MS = 120_000

export const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

apiClient.interceptors.request.use(
  (config) => {
    const baseUrl = getApiBaseUrl()

    if (config.url && (config.url.startsWith('http://') || config.url.startsWith('https://'))) {
      config.baseURL = ''
    } else if (!config.baseURL || config.baseURL !== baseUrl) {
      config.baseURL = baseUrl
    }

    const authPort = tryGetAuthPort()
    const token = authPort?.getToken() ?? null
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      // Дефолтный Content-Type: application/json заставлял axios сериализовать FormData в JSON,
      // и файлы терялись («No file uploaded»). Без заголовка браузер сам выставит boundary.
      config.headers.delete('Content-Type')
      // Загрузка до 10 МБ на медленном канале не укладывается в общие 30 секунд
      config.timeout = Math.max(config.timeout ?? 0, UPLOAD_TIMEOUT_MS)
    }

    try {
      SentryService.addBreadcrumb(
        `API Request: ${config.method?.toUpperCase()} ${config.url}`,
        'http',
        {
          method: config.method,
          url: config.url,
        },
      )
    } catch (error) {
      console.warn('Failed to add Sentry breadcrumb:', error)
    }

    return config
  },
  (error) => {
    try {
      SentryService.captureException(error, {
        context: 'api_request',
        url: error.config?.url,
        method: error.config?.method,
      })
    } catch (sentryError) {
      console.warn('Failed to capture error in Sentry:', sentryError)
    }
    return Promise.reject(error)
  },
)

let isRefreshing = false
let refreshPromise: Promise<string | null> | null = null
let pendingRequestsQueue: Array<{
  resolve: (value: unknown) => void
  reject: (reason: unknown) => void
  config: AxiosRequestConfig
}> = []

type ApiTokenResponse = {
  meta?: { token?: string }
  data?: { token?: string }
  token?: string
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const performRefresh = async (): Promise<string | null> => {
  try {
    console.log('🔄 Attempting to refresh token...')
    const { data } = await apiClient.post(
      '/api/auth/refresh',
      {},
      {
        timeout: 10000,
        withCredentials: true,
      },
    )

    const responseData = data as ApiTokenResponse
    const newToken = responseData?.meta?.token ?? responseData?.data?.token ?? responseData?.token
    if (!newToken) {
      throw new Error('No refresh token received')
    }

    console.log('✅ Token refreshed successfully')
    return newToken
  } catch (error) {
    console.error('❌ Token refresh failed:', error)
    throw error
  }
}

const processPendingRequests = (token: string | null, error?: unknown) => {
  pendingRequestsQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error)
    } else if (token) {
      if (!config.headers) config.headers = {}
      config.headers.Authorization = `Bearer ${token}`
      apiClient.request(config).then(resolve).catch(reject)
    } else {
      reject(new Error('No token available'))
    }
  })
  pendingRequestsQueue = []
}

apiClient.interceptors.response.use(
  (response) => {
    try {
      SentryService.addBreadcrumb(
        `API Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`,
        'http',
        {
          status: response.status,
          method: response.config.method,
          url: response.config.url,
        },
      )
    } catch (error) {
      console.warn('Failed to add Sentry breadcrumb:', error)
    }

    return response
  },
  async (error: AxiosError) => {
    const authPort = tryGetAuthPort()
    const status = error.response?.status
    const originalRequest = error.config

    if (status === 401 && originalRequest && authPort) {
      if (isAuthEndpoint(originalRequest.url)) {
        const earlyResponseData = error.response?.data
        if (isRecord(earlyResponseData) && typeof earlyResponseData['message'] === 'string') {
          error.message = earlyResponseData['message']
        } else if (isRecord(earlyResponseData) && typeof earlyResponseData['error'] === 'string') {
          error.message = earlyResponseData['error']
        }
        if (originalRequest.url?.includes('/auth/refresh')) {
          authPort.onRefreshFailed()
        }
        return Promise.reject(error)
      }

      const currentToken = authPort.getToken()
      if (originalRequest.headers?.Authorization === `Bearer ${currentToken}`) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            pendingRequestsQueue.push({ resolve, reject, config: originalRequest })
          })
        }

        isRefreshing = true

        try {
          if (!refreshPromise) {
            refreshPromise = performRefresh()
          }

          const newToken = await refreshPromise

          if (newToken) {
            authPort.setToken(newToken)
            processPendingRequests(newToken)

            if (!originalRequest.headers) originalRequest.headers = {} as AxiosRequestHeaders
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return apiClient.request(originalRequest)
          }

          throw new Error('Failed to refresh token')
        } catch (refreshError) {
          console.error('Refresh failed, logging out:', refreshError)
          processPendingRequests(null, refreshError)
          authPort.onRefreshFailed()
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
          refreshPromise = null
        }
      }

      return apiClient.request(originalRequest)
    }

    try {
      SentryService.captureException(error, {
        context: 'api_response',
        url: originalRequest?.url,
        method: originalRequest?.method,
        status: status,
        response_data: error.response?.data,
      })

      SentryService.addBreadcrumb(
        `API Error: ${status} ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`,
        'http',
        {
          status: status,
          method: originalRequest?.method,
          url: originalRequest?.url,
          error_message: error.message,
        },
      )
    } catch (sentryError) {
      console.warn('Failed to capture error in Sentry:', sentryError)
    }

    const responseData = error.response?.data
    if (isRecord(responseData) && typeof responseData['message'] === 'string') {
      error.message = responseData['message']
    } else if (isRecord(responseData) && typeof responseData['error'] === 'string') {
      error.message = responseData['error']
    } else {
      const friendly = friendlyHttpMessage(error)
      if (friendly !== null) error.message = friendly
    }

    return Promise.reject(error)
  },
)

export default apiClient

export { apiErrorData, apiErrorMessage } from '@/shared/api/errors'

export function resetRefreshStateForTests(): void {
  isRefreshing = false
  refreshPromise = null
  pendingRequestsQueue = []
}

export function getRefreshStateForTests(): {
  isRefreshing: boolean
  pendingCount: number
} {
  return {
    isRefreshing,
    pendingCount: pendingRequestsQueue.length,
  }
}

export function isAuthEndpointForTests(url: string | undefined): boolean {
  return isAuthEndpoint(url)
}

export function getAuthPortForTests(): typeof getAuthPort {
  return getAuthPort
}
